import { supabase } from '@/integrations/supabase/client';
import { getAutoChargeDefaults, hasDefaultStripeCard } from '@/lib/autoCharge';
import { addMonths, format } from 'date-fns';
import { safeFormat } from '@/lib/safeDate';

interface CreateSaleForCaseParams {
  caseId: string;
  catalogId: string;
  totalAmount: number;
  installments: number;
  firstDueDate: Date;
}

export interface CustomInstallment {
  date: string;
  amount: number;
  isDownPayment?: boolean;
}

export interface PaymentWithMeta extends CustomInstallment {
  markedAsPaid?: boolean;
  paymentMethod?: string;
  paymentDate?: string;
  reference?: string;
}

interface CreateStandaloneSaleParams {
  contactId: string;
  catalogId: string;
  totalAmount: number;
  customInstallments?: PaymentWithMeta[];
  userId?: string; // ID do usuário que está registrando a venda
  // Legacy params (used if customInstallments not provided)
  installments?: number;
  downPayment?: number;
  firstDueDate?: Date;
}

export function useCreateSale() {
  /**
   * Adiciona um serviço a um case existente
   * - Busca o deal_id do case
   * - Cria item com status = 'sold'
   * - Cria payments (parcelas)
   * - Atualiza deals.total_amount
   */
  async function createSaleForCase({
    caseId,
    catalogId,
    totalAmount,
    installments,
    firstDueDate
  }: CreateSaleForCaseParams) {
    // 1. Buscar o deal_id via case → item → deal
    const { data: caseData, error: caseError } = await supabase
      .from('cases')
      .select('item_id, item:items(deal_id, deal:deals(id, total_amount, contact_id, contacts:contacts!fk_deals_contact(stripe_default_payment_method_id)))')
      .eq('id', caseId)
      .single();

    if (caseError) throw new Error(`Erro ao buscar case: ${caseError.message}`);
    if (!caseData?.item?.deal_id) throw new Error('Case não possui deal vinculado');

    const dealId = caseData.item.deal_id;
    const currentDealTotal = caseData.item.deal?.total_amount || 0;
    const hasDefaultCard = hasDefaultStripeCard(caseData.item.deal?.contacts);

    // 2. Buscar info do catálogo
    const { data: catalogItem, error: catalogError } = await supabase
      .from('catalog')
      .select('name, base_price')
      .eq('id', catalogId)
      .single();

    if (catalogError) throw new Error(`Erro ao buscar catálogo: ${catalogError.message}`);

    // 3. Criar item
    const { data: newItem, error: itemError } = await supabase
      .from('items')
      .insert([{
        deal_id: dealId,
        catalog_id: catalogId,
        status: 'sold',
        quantity: 1,
        unit_price: totalAmount,
        discount_percent: 0,
        discount_amount: 0,
        notes: `Serviço adicionado via jurídico`
      }])
      .select()
      .single();

    if (itemError) throw new Error(`Erro ao criar item: ${itemError.message}`);

    // 4. Criar payments (parcelas)
    const installmentAmount = totalAmount / installments;
    const payments: {
      deal_id: string;
      amount: number;
      due_date: string;
      status: string;
      installment_number: number;
      payment_type: string;
      description: string;
      auto_charge_enabled: boolean;
      auto_charge_status: string | null;
      auto_charge_attempts: number;
    }[] = [];

    for (let i = 0; i < installments; i++) {
      const dueDate = addMonths(firstDueDate, i);
      payments.push({
        deal_id: dealId,
        amount: installmentAmount,
        due_date: safeFormat(dueDate, 'yyyy-MM-dd'),
        status: 'pending',
        installment_number: i + 1,
        payment_type: 'installment',
        description: `Parcela ${i + 1}/${installments} - ${catalogItem?.name || 'Serviço'}`,
        ...getAutoChargeDefaults(hasDefaultCard),
      });
    }

    const { error: paymentsError } = await supabase
      .from('payments')
      .insert(payments);

    if (paymentsError) throw new Error(`Erro ao criar parcelas: ${paymentsError.message}`);

    // 5. Atualizar total_amount do deal
    const newTotal = currentDealTotal + totalAmount;
    const { error: updateError } = await supabase
      .from('deals')
      .update({ total_amount: newTotal })
      .eq('id', dealId);

    if (updateError) throw new Error(`Erro ao atualizar deal: ${updateError.message}`);

    return { item: newItem, dealId };
  }

  /**
   * Cria uma venda avulsa para um contato
   * - Cria deal com stage='WON', source='direct'
   * - Cria item com status = 'sold'
   * - Cria payments (parcelas)
   * - Cria payment_transactions para parcelas marcadas como pagas (pending_confirmation)
   */
  async function createStandaloneSale({
    contactId,
    catalogId,
    totalAmount,
    customInstallments,
    userId,
    installments = 1,
    downPayment = 0,
    firstDueDate
  }: CreateStandaloneSaleParams) {
    // 1. Buscar info do catálogo
    const { data: contactData, error: contactError } = await supabase
      .from('contacts')
      .select('stripe_default_payment_method_id')
      .eq('id', contactId)
      .single();

    if (contactError) throw new Error(`Erro ao buscar contato: ${contactError.message}`);

    const hasDefaultCard = hasDefaultStripeCard(contactData);

    // 1. Buscar info do catálogo
    const { data: catalogItem, error: catalogError } = await supabase
      .from('catalog')
      .select('name, base_price')
      .eq('id', catalogId)
      .single();

    if (catalogError) throw new Error(`Erro ao buscar catálogo: ${catalogError.message}`);

    // 2. Criar deal with both subtotal_amount and total_amount
    const { data: newDeal, error: dealError } = await supabase
      .from('deals')
      .insert({
        contact_id: contactId,
        name: catalogItem?.name || 'Venda Direta',
        stage: 'won',
        source: 'direct',
        deals_source: 'Upsell',
        deals_channel: 'Phone Call',
        subtotal_amount: totalAmount,  // NEW: subtotal before any discounts
        total_amount: totalAmount,     // Same as subtotal initially (no discount)
        closed_date: safeFormat(new Date(), 'yyyy-MM-dd')
      })
      .select()
      .single();

    if (dealError) throw new Error(`Erro ao criar deal: ${dealError.message}`);

    // 3. Criar item
    const { data: newItem, error: itemError } = await supabase
      .from('items')
      .insert([{
        deal_id: newDeal.id,
        catalog_id: catalogId,
        status: 'sold',
        quantity: 1,
        unit_price: totalAmount,
        discount_percent: 0,
        discount_amount: 0,
        notes: 'Venda direta via jurídico'
      }])
      .select()
      .single();

    if (itemError) throw new Error(`Erro ao criar item: ${itemError.message}`);

    // 3.5 Criar registro de serviço para controle de entrega
    let newService = null;
    const { data: serviceData, error: serviceError } = await supabase
      .from('services')
      .insert({
        item_id: newItem.id,
        contact_id: contactId,
        service_type: catalogItem?.name || 'Serviço',
        status: 'pending',
        assigned_to_user_id: userId || null,
        notes: 'Criado via registro de venda'
      })
      .select()
      .single();

    if (serviceError) {
      console.error('Erro ao criar serviço:', serviceError);
      // Não falhar a venda, apenas logar
    } else {
      newService = serviceData;
    }

    // 4. Criar payments
    const paymentsToInsert: {
      deal_id: string;
      amount: number;
      due_date: string;
      status: string;
      installment_number: number;
      payment_type: string;
      description: string;
      auto_charge_enabled: boolean;
      auto_charge_status: string | null;
      auto_charge_attempts: number;
    }[] = [];

    // Track which payments need transactions (marked as paid)
    const markedPaidInstallments: { index: number; meta: PaymentWithMeta }[] = [];

    // Use custom installments if provided
    if (customInstallments && customInstallments.length > 0) {
      customInstallments.forEach((inst, i) => {
        paymentsToInsert.push({
          deal_id: newDeal.id,
          amount: inst.amount,
          due_date: inst.date,
          status: 'pending',
          installment_number: i + 1,
          payment_type: inst.isDownPayment ? 'down_payment' : 'installment',
          description: inst.isDownPayment 
            ? `Entrada - ${catalogItem?.name || 'Serviço'}` 
            : `Parcela ${i + 1}/${customInstallments.length} - ${catalogItem?.name || 'Serviço'}`,
          ...getAutoChargeDefaults(hasDefaultCard),
        });

        if (inst.markedAsPaid) {
          markedPaidInstallments.push({ index: i, meta: inst });
        }
      });
    } else if (firstDueDate) {
      // Legacy: generate installments automatically
      let remainingAmount = totalAmount;

      if (downPayment > 0) {
        paymentsToInsert.push({
          deal_id: newDeal.id,
          amount: downPayment,
          due_date: safeFormat(new Date(), 'yyyy-MM-dd'),
          status: 'pending',
          installment_number: 1,
          payment_type: 'down_payment',
          description: `Entrada - ${catalogItem?.name || 'Serviço'}`,
          ...getAutoChargeDefaults(hasDefaultCard),
        });
        remainingAmount -= downPayment;
      }

      const remainingInstallments = downPayment > 0 ? installments - 1 : installments;
      if (remainingInstallments > 0 && remainingAmount > 0) {
        const installmentAmount = remainingAmount / remainingInstallments;

        for (let i = 0; i < remainingInstallments; i++) {
          const dueDate = addMonths(firstDueDate, i);
          paymentsToInsert.push({
            deal_id: newDeal.id,
            amount: installmentAmount,
            due_date: safeFormat(dueDate, 'yyyy-MM-dd'),
            status: 'pending',
            installment_number: downPayment > 0 ? i + 2 : i + 1,
            payment_type: 'installment',
            description: `Parcela ${i + 1}/${remainingInstallments} - ${catalogItem?.name || 'Serviço'}`,
            ...getAutoChargeDefaults(hasDefaultCard),
          });
        }
      }
    }

    let createdPayments: { id: string }[] = [];
    if (paymentsToInsert.length > 0) {
      const { data: paymentsData, error: paymentsError } = await supabase
        .from('payments')
        .insert(paymentsToInsert)
        .select('id');

      if (paymentsError) throw new Error(`Erro ao criar parcelas: ${paymentsError.message}`);
      createdPayments = paymentsData || [];
    }

    // 5. Create payment_transactions for marked as paid (pending_confirmation)
    if (markedPaidInstallments.length > 0 && createdPayments.length > 0) {
      const transactions = markedPaidInstallments.map(({ index, meta }) => ({
        payment_id: createdPayments[index]?.id,
        deal_id: newDeal.id,
        amount: meta.amount,
        payment_method: meta.paymentMethod || 'cash',
        transaction_date: meta.paymentDate || safeFormat(new Date(), 'yyyy-MM-dd'),
        status: 'pending_confirmation',
        notes: meta.reference || 'Pagamento registrado na venda',
        recorded_by: userId || null,
        trigger_type: 'manual'
      })).filter(t => t.payment_id);

      if (transactions.length > 0) {
        const { error: txError } = await supabase
          .from('payment_transactions')
          .insert(transactions);

        if (txError) {
          console.error('Erro ao criar transações:', txError);
          // Don't throw - sale was created, just log the error
        }
      }
    }

    return { deal: newDeal, item: newItem, dealId: newDeal.id, serviceId: newService?.id };
  }

  return {
    createSaleForCase,
    createStandaloneSale
  };
}
